import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { all_routes } from '../../../router/all_routes';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showSuccessAlert, showItemExistsError } from '../../../../core/alert/sweetAlertConfig';
import { Button } from 'react-bootstrap';

interface LocationDTO { id: number; name: string; address: string; lat: number; lng: number; description?: string; createdBy: string; createdAt: string; updatedBy?: string; updatedAt?: string; }
interface BuildingDTO { id: number; name: string; noOfFloors: number; locationName: string; locationId: number; description?: string | null; }
interface FloorDTO { id: number; name: string; floorNumber: number; buildingId: number; buildingName: string; locationName: string; createdBy: string; createdAt: string; updatedBy?: string; updatedAt?: string; }
interface RoomDTO { id: number; name: string; floorId: number; buildingId: number; status: string; createdBy: string; createdAt: string; updatedBy?: string; updatedAt?: string; description?: string; }
interface ItemBrand { id: number; brandName: string; description: string; }
interface ItemType { id: number; itemType: string; description: string; }

interface AddItemModuleProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  locations: LocationDTO[];
  buildings: BuildingDTO[];
  floors: FloorDTO[];
  rooms: RoomDTO[];
  itemBrands: ItemBrand[];
  itemTypes: ItemType[];
}

type ScheduleType = '' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'CUSTOM';
type ItemStatus = 'ACTIVE' | 'INACTIVE' | 'COMPLETED';

const AddItemModule: React.FC<AddItemModuleProps> = ({
  show,
  onHide,
  onSuccess,
  locations,
  buildings: buildingsProp = [],
  floors: floorsProp = [],
  rooms: roomsProp = [],
  itemBrands,
  itemTypes,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // local, do not mutate props
  const [availableBuildings, setAvailableBuildings] = useState<BuildingDTO[]>(buildingsProp || []);
  const [availableFloors, setAvailableFloors] = useState<FloorDTO[]>(floorsProp || []);
  const [availableRooms, setAvailableRooms] = useState<RoomDTO[]>(roomsProp || []);

  const [newItem, setNewItem] = useState({
    itemName: '',
    brandId: '',
    description: '',
    capacity: '',
    itemTypeId: '',
    locationId: '',
    buildingId: '',
    floorId: '',
    roomId: '',
    isServiceRequired: false,
    scheduleType: '' as ScheduleType,
    startDate: '',      // YYYY-MM-DD
    endDate: '',        // optional
    intervalMonths: '', // number (string for input)
    // NEW FIELDS
    serialNumber: '',
    purchaseDate: '',      // YYYY-MM-DD (<= today)
    usingSinceDate: '',    // YYYY-MM-DD (<= today)
    warrantyEndDate: '',   // YYYY-MM-DD (>= today)
    status: 'ACTIVE' as ItemStatus,
  });

  const todayStr = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  // Convert "YYYY-MM-DD" → "YYYY-MM-DDT00:00:00Z"
  const toStartOfDayZ = (dateStr?: string) => {
    if (!dateStr) return undefined;
    return `${dateStr}T00:00:00Z`;
  };

  /* ------------------- Cascading selects ------------------- */
  const handleLocationChange = async (locationId: string) => {
    setNewItem(prev => ({ ...prev, locationId, buildingId: '', floorId: '', roomId: '' }));
    setAvailableBuildings([]); setAvailableFloors([]); setAvailableRooms([]);
    if (!locationId) return;

    try {
      setLoading(true);
      const res: AxiosResponse = await apiDefinitions.getBuildingsByLocationId(Number(locationId));
      if (res.status === 200) {
        setAvailableBuildings(res.data?.data || []);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch buildings');
      }
    } catch (e: any) {
      showError(e?.response?.data?.statusDescription || 'Error fetching buildings');
    } finally {
      setLoading(false);
    }
  };

  const handleBuildingChange = async (buildingId: string) => {
    setNewItem(prev => ({ ...prev, buildingId, floorId: '', roomId: '' }));
    setAvailableFloors([]); setAvailableRooms([]);
    if (!buildingId) return;

    try {
      setLoading(true);
      const res: AxiosResponse = await apiDefinitions.getFloorsByBuildingId(Number(buildingId));
      if (res.status === 200) {
        setAvailableFloors(res.data?.data || []);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch floors');
      }
    } catch (e: any) {
      showError(e?.response?.data?.statusDescription || 'Error fetching floors');
    } finally {
      setLoading(false);
    }
  };

  const handleFloorChange = async (floorId: string) => {
    setNewItem(prev => ({ ...prev, floorId, roomId: '' }));
    setAvailableRooms([]);
    if (!floorId) return;

    try {
      setLoading(true);
      const res: AxiosResponse = await apiDefinitions.getRoomsByFloorId(Number(floorId));
      if (res.status === 200) {
        setAvailableRooms(res.data?.data || []);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch rooms');
      }
    } catch (e: any) {
      showError(e?.response?.data?.statusDescription || 'Error fetching rooms');
    } finally {
      setLoading(false);
    }
  };

  /* ------------------- Save ------------------- */
  const handleAddItem = async () => {
    // Required basics
    if (!newItem.itemName.trim() || !newItem.description.trim() || !newItem.brandId || !newItem.itemTypeId) {
      showError('Item name, description, brand, and type are required.');
      return;
    }

    // Date validations
    if (newItem.purchaseDate && newItem.purchaseDate > todayStr) {
      showError('Purchase date cannot be in the future.');
      return;
    }
    if (newItem.usingSinceDate && newItem.usingSinceDate > todayStr) {
      showError('Using since date cannot be in the future.');
      return;
    }
    if (newItem.warrantyEndDate && newItem.warrantyEndDate < todayStr) {
      showError('Warranty end date must be today or a future date.');
      return;
    }

    // Optional: sanity check logical order (not requested, but safe)
    if (newItem.purchaseDate && newItem.usingSinceDate && newItem.usingSinceDate < newItem.purchaseDate) {
      showError('Using since date cannot be before purchase date.');
      return;
    }
    if (newItem.purchaseDate && newItem.warrantyEndDate && newItem.warrantyEndDate < newItem.purchaseDate) {
      showError('Warranty end date cannot be before purchase date.');
      return;
    }

    // Service validations (mirror backend)
    if (newItem.isServiceRequired) {
      if (!newItem.scheduleType) {
        showError('Schedule type is required when service is required.');
        return;
      }
      if (!newItem.startDate) {
        showError('Start date is required when service is required.');
        return;
      }
      if (newItem.scheduleType === 'CUSTOM') {
        const iv = Number(newItem.intervalMonths);
        if (!iv || iv < 1) {
          showError('Interval months must be a positive number for Custom schedule.');
          return;
        }
      } else if (newItem.intervalMonths) {
        showError('Interval months is only allowed for Custom schedule.');
        return;
      }
      if (newItem.startDate < todayStr) {
        showError('Start date must be today or later.');
        return;
      }
      if (newItem.endDate && !(newItem.endDate > newItem.startDate)) {
        showError('End date must be after start date.');
        return;
      }
    }

    const token = localStorage.getItem('token');
    if (!token) {
      showError('No authentication token found. Please log in.');
      navigate(all_routes.login);
      return;
    }

    try {
      setLoading(true);

      const payload: any = {
        itemName: newItem.itemName.trim(),
        brandId: Number(newItem.brandId),
        description: newItem.description.trim(),
        capacity: newItem.capacity || '',
        itemTypeId: Number(newItem.itemTypeId),
        isServiceRequired: !!newItem.isServiceRequired,
        // NEW FIELDS (match your backend builder)
        serialNumber: newItem.serialNumber?.trim() || null,
        purchaseDate: newItem.purchaseDate ? toStartOfDayZ(newItem.purchaseDate) : null,
        usingSinceDate: newItem.usingSinceDate ? toStartOfDayZ(newItem.usingSinceDate) : null,
        warrantyEndDate: newItem.warrantyEndDate ? toStartOfDayZ(newItem.warrantyEndDate) : null,
        status: newItem.status || 'ACTIVE',
      };

      if (newItem.roomId) payload.roomId = Number(newItem.roomId);
      else if (newItem.floorId) payload.floorId = Number(newItem.floorId);
      else if (newItem.buildingId) payload.buildingId = Number(newItem.buildingId);

      if (newItem.isServiceRequired) {
        payload.scheduleType = newItem.scheduleType; // DAILY|WEEKLY|MONTHLY|YEARLY|CUSTOM
        payload.startDate = newItem.startDate;       // "YYYY-MM-DD"
        if (newItem.endDate) payload.endDate = newItem.endDate;
        if (newItem.scheduleType === 'CUSTOM') {
          payload.intervalMonths = Number(newItem.intervalMonths);
        }
      }

      const response = await apiDefinitions.createItem(payload);
      if (response.data?.status === 'S1000') {
        showSuccessAlert("Item added successfully");
        onSuccess();
        setNewItem({
          itemName: '',
          brandId: '',
          description: '',
          capacity: '',
          itemTypeId: '',
          locationId: '',
          buildingId: '',
          floorId: '',
          roomId: '',
          isServiceRequired: false,
          scheduleType: '',
          startDate: '',
          endDate: '',
          intervalMonths: '',
          serialNumber: '',
          purchaseDate: '',
          usingSinceDate: '',
          warrantyEndDate: '',
          status: 'ACTIVE',
        });
        setAvailableBuildings([]); setAvailableFloors([]); setAvailableRooms([]);
        onHide();
      } else {
        showError(response.data?.statusDescription || 'Failed to add item');
      }
    } catch (error: any) {
      if (error.response?.status === 409) {
        showItemExistsError("Item with this name already exists in the selected location");
      } else if (error.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else {
        showError(error.response?.data?.statusDescription || 'Error adding item');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ------------------- Render ------------------- */
  return (
    <div className={`modal fade ${show ? 'show d-block' : ''}`} role="dialog" style={{ background: show ? 'rgba(0,0,0,0.5)' : 'none' }}>
      {/* Scoped styles for 2-column display */}
      <style>{`
        .form-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
        @media (min-width: 768px) { .form-grid { grid-template-columns: 1fr 1fr; } }
        .full-span { grid-column: 1 / -1; }
        .section-title { font-weight: 600; color: #333; }
      `}</style>

      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div className="modal-content" style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <div className="modal-header" style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
            <h5 className="modal-title" style={{ color: '#333', fontSize: '1.1rem' }}>Add Item</h5>
            <button className="btn-close" onClick={onHide} aria-label="Close" />
          </div>

          <div className="modal-body" style={{ padding: '1.5rem' }}>
            {/* ---------- Basics (2 columns) ---------- */}
            <div className="mb-2 section-title">Basic Info</div>
            <div className="form-grid">
              <div className="full-span">
                <label className="col-form-label">Item Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={newItem.itemName}
                  onChange={(e) => setNewItem({ ...newItem, itemName: e.target.value })}
                />
              </div>

              <div>
                <label className="col-form-label">Brand <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={newItem.brandId}
                  onChange={(e) => setNewItem({ ...newItem, brandId: e.target.value })}
                >
                  <option value="">Select Brand</option>
                  {itemBrands.map(b => <option key={b.id} value={b.id}>{b.brandName}</option>)}
                </select>
              </div>

              <div>
                <label className="col-form-label">Item Type <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={newItem.itemTypeId}
                  onChange={(e) => setNewItem({ ...newItem, itemTypeId: e.target.value })}
                >
                  <option value="">Select Type</option>
                  {itemTypes.map(t => <option key={t.id} value={t.id}>{t.itemType}</option>)}
                </select>
              </div>

              <div className="full-span">
                <label className="col-form-label">Description <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                />
              </div>

              <div>
                <label className="col-form-label">Capacity</label>
                <input
                  type="text"
                  className="form-control"
                  value={newItem.capacity}
                  onChange={(e) => setNewItem({ ...newItem, capacity: e.target.value })}
                />
              </div>

              <div>
                <label className="col-form-label">Serial Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={newItem.serialNumber}
                  onChange={(e) => setNewItem({ ...newItem, serialNumber: e.target.value })}
                  placeholder="e.g., DC1234567"
                />
              </div>
            </div>

            <hr className="my-4" />

            {/* ---------- Lifecycle / Status (2 columns) ---------- */}
            <div className="mb-2 section-title">Lifecycle & Status</div>
            <div className="form-grid">
              <div>
                <label className="col-form-label">Purchase Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={newItem.purchaseDate}
                  onChange={(e) => setNewItem({ ...newItem, purchaseDate: e.target.value })}
                  max={todayStr}
                />
              </div>

              <div>
                <label className="col-form-label">Using Since Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={newItem.usingSinceDate}
                  onChange={(e) => setNewItem({ ...newItem, usingSinceDate: e.target.value })}
                  max={todayStr}
                />
              </div>

              <div>
                <label className="col-form-label">Warranty End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={newItem.warrantyEndDate}
                  onChange={(e) => setNewItem({ ...newItem, warrantyEndDate: e.target.value })}
                  min={todayStr}
                />
              </div>

              <div>
                <label className="col-form-label">Status</label>
                <select
                  className="form-select"
                  value={newItem.status}
                  onChange={(e) => setNewItem({ ...newItem, status: e.target.value as ItemStatus })}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>
            </div>

            <hr className="my-4" />

            {/* ---------- Location (2 columns) ---------- */}
            <div className="mb-2 section-title">Location</div>
            <div className="form-grid">
              <div>
                <label className="col-form-label">Location <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={newItem.locationId}
                  onChange={(e) => handleLocationChange(e.target.value)}
                >
                  <option value="">Select Location</option>
                  {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                </select>
              </div>

              <div>
                <label className="col-form-label">Building <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={newItem.buildingId}
                  onChange={(e) => handleBuildingChange(e.target.value)}
                  disabled={!availableBuildings.length}
                >
                  <option value="">{availableBuildings.length ? 'Select Building' : 'Select a location first'}</option>
                  {availableBuildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>

              <div>
                <label className="col-form-label">Floor</label>
                <select
                  className="form-select"
                  value={newItem.floorId}
                  onChange={(e) => handleFloorChange(e.target.value)}
                  disabled={!availableFloors.length}
                >
                  <option value="">{availableFloors.length ? 'Select Floor' : 'Select a building first'}</option>
                  {availableFloors.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>

              <div>
                <label className="col-form-label">Room</label>
                <select
                  className="form-select"
                  value={newItem.roomId}
                  onChange={(e) => setNewItem({ ...newItem, roomId: e.target.value })}
                  disabled={!availableRooms.length}
                >
                  <option value="">{availableRooms.length ? 'Select Room' : 'Select a floor first'}</option>
                  {availableRooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            </div>

            <hr className="my-4" />

            {/* ---------- Service (2 columns) ---------- */}
            <div className="mb-2 section-title">Service</div>
            <div className="form-grid">
              <div className="full-span">
                <div className="form-check form-switch">
                  <input
                    id="isServiceRequired"
                    className="form-check-input"
                    type="checkbox"
                    checked={newItem.isServiceRequired}
                    onChange={(e) =>
                      setNewItem(prev => ({
                        ...prev,
                        isServiceRequired: e.target.checked,
                        ...(e.target.checked ? {} : { scheduleType: '', startDate: '', endDate: '', intervalMonths: '' })
                      }))
                    }
                  />
                  <label className="form-check-label ms-2" htmlFor="isServiceRequired">Service required</label>
                </div>
              </div>

              {newItem.isServiceRequired && (
                <>
                  <div>
                    <label className="col-form-label">Schedule Type <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={newItem.scheduleType}
                      onChange={(e) => {
                        const val = e.target.value as ScheduleType;
                        setNewItem(prev => ({
                          ...prev,
                          scheduleType: val,
                          intervalMonths: val === 'CUSTOM' ? prev.intervalMonths : '',
                        }));
                      }}
                    >
                      <option value="">Select Schedule</option>
                      <option value="DAILY">Daily</option>
                      <option value="WEEKLY">Weekly</option>
                      <option value="MONTHLY">Monthly</option>
                      <option value="YEARLY">Yearly</option>
                      <option value="CUSTOM">Custom (every N months)</option>
                    </select>
                  </div>

                  <div>
                    <label className="col-form-label">Start Date <span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      value={newItem.startDate}
                      onChange={(e) => setNewItem({ ...newItem, startDate: e.target.value })}
                      min={todayStr}
                    />
                  </div>

                  <div>
                    <label className="col-form-label">End Date (optional)</label>
                    <input
                      type="date"
                      className="form-control"
                      value={newItem.endDate}
                      onChange={(e) => setNewItem({ ...newItem, endDate: e.target.value })}
                      min={newItem.startDate || todayStr}
                    />
                  </div>

                  {newItem.scheduleType === 'CUSTOM' && (
                    <div>
                      <label className="col-form-label">Interval (months) <span className="text-danger">*</span></label>
                      <input
                        type="number"
                        className="form-control"
                        min={1}
                        step={1}
                        placeholder="e.g., 3"
                        value={newItem.intervalMonths}
                        onChange={(e) => setNewItem({ ...newItem, intervalMonths: e.target.value })}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

          </div>

          <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
            <Button variant="primary" onClick={onHide}>Cancel</Button>
            <Button variant="success" onClick={handleAddItem} disabled={loading}>
              {loading ? <span className="spinner-border spinner-border-sm me-2" /> : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddItemModule;
